import { useEffect, useState } from "react";
import { UsersAPI, ProfileAPI } from "../lib/api";
import { formatDate } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, UserPlus, Trash, Key, Prohibit, CheckCircle, Copy, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
};

export default function Subcontractors() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [deleteId, setDeleteId] = useState(null);
  const [resetInfo, setResetInfo] = useState(null); // {email, newPassword}
  const [profileView, setProfileView] = useState(null); // {user, profile}

  const load = () => {
    setLoading(true);
    UsersAPI.list()
      .then(setUsers)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const subs = users.filter((u) => u.role === "subcontractor");

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await UsersAPI.create(form);
      toast.success(`Subcontractor ${form.name} created`);
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (u) => {
    try {
      await UsersAPI.toggleActive(u.id);
      toast.success(u.active ? "Account disabled" : "Account enabled");
      load();
    } catch {
      toast.error("Failed to update account");
    }
  };

  const handleReset = async (u) => {
    try {
      const { new_password } = await UsersAPI.resetPassword(u.id);
      setResetInfo({ email: u.email, newPassword: new_password });
    } catch {
      toast.error("Failed to reset password");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await UsersAPI.remove(deleteId);
      toast.success("Account deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Failed to delete account");
    }
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleViewProfile = async (u) => {
    try {
      const p = await ProfileAPI.getFor(u.id);
      setProfileView({ user: u, profile: p });
    } catch {
      toast.error("Failed to load profile");
    }
  };

  return (
    <div data-testid="subcontractors-page">
      <PageHeader
        overline="Admin"
        title="Subcontractors"
        description="Create and manage subcontractor logins. Each sees only their own invoices."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-sub-btn">
                <UserPlus size={16} weight="bold" className="mr-2" />
                New Subcontractor
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-sub-dialog">
              <DialogHeader>
                <DialogTitle>New Subcontractor</DialogTitle>
                <DialogDescription>
                  Create a login. Share the email and password with the subcontractor.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="sub-name" className="overline">Full Name</Label>
                  <Input
                    id="sub-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="rounded-sm mt-1.5"
                    data-testid="sub-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sub-email" className="overline">Email</Label>
                  <Input
                    id="sub-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="rounded-sm mt-1.5"
                    data-testid="sub-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sub-password" className="overline">Starting Password</Label>
                  <Input
                    id="sub-password"
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="min 6 characters"
                    className="rounded-sm mt-1.5 font-mono"
                    data-testid="sub-password-input"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="rounded-sm"
                    data-testid="sub-save-btn"
                  >
                    {saving ? "Creating…" : "Create Account"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="border border-border bg-white">
        <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-muted/40 overline text-muted-foreground">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : subs.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-sm text-muted-foreground mb-4">
              No subcontractors yet.
            </div>
            <Button onClick={() => setOpen(true)} className="rounded-sm" data-testid="empty-add-sub-btn">
              <Plus size={16} weight="bold" className="mr-2" />
              Add your first subcontractor
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {subs.map((u) => (
              <div
                key={u.id}
                className="px-4 sm:px-6 py-4 flex flex-col sm:grid sm:grid-cols-12 gap-2 sm:gap-4 sm:items-center"
                data-testid={`sub-row-${u.email}`}
              >
                <div className="sm:col-span-3 font-semibold text-sm">{u.name}</div>
                <div className="sm:col-span-3 text-sm text-muted-foreground truncate">
                  {u.email}
                </div>
                <div className="sm:col-span-2 text-xs text-muted-foreground">
                  {formatDate(u.created_at)}
                </div>
                <div className="sm:col-span-1">
                  <span
                    className={`inline-block text-xs font-semibold px-2 py-0.5 border ${
                      u.active
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-muted text-muted-foreground border-border"
                    }`}
                  >
                    {u.active ? "Active" : "Disabled"}
                  </span>
                </div>
                <div className="sm:col-span-3 flex justify-start sm:justify-end gap-1">
                  <button
                    onClick={() => handleViewProfile(u)}
                    className="p-1.5 hover:bg-muted rounded-sm"
                    data-testid={`view-profile-${u.email}`}
                    aria-label="View profile"
                    title="View profile & bank details"
                  >
                    <Eye size={16} weight="bold" />
                  </button>
                  <button
                    onClick={() => handleReset(u)}
                    className="p-1.5 hover:bg-muted rounded-sm"
                    data-testid={`reset-${u.email}`}
                    aria-label="Reset password"
                    title="Reset password"
                  >
                    <Key size={16} weight="bold" />
                  </button>
                  <button
                    onClick={() => handleToggle(u)}
                    className="p-1.5 hover:bg-muted rounded-sm"
                    data-testid={`toggle-${u.email}`}
                    aria-label="Toggle active"
                    title={u.active ? "Disable" : "Enable"}
                  >
                    {u.active ? <Prohibit size={16} weight="bold" /> : <CheckCircle size={16} weight="bold" />}
                  </button>
                  <button
                    onClick={() => setDeleteId(u.id)}
                    className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                    data-testid={`delete-${u.email}`}
                    aria-label="Delete"
                    title="Delete account"
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View subcontractor profile */}
      <Dialog open={!!profileView} onOpenChange={(o) => !o && setProfileView(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-profile-dialog">
          <DialogHeader>
            <DialogTitle>{profileView?.user?.name}'s Profile</DialogTitle>
            <DialogDescription>
              Business details and bank info — this appears on every invoice they issue.
            </DialogDescription>
          </DialogHeader>
          {profileView && (
            <div className="space-y-5 text-sm">
              <div>
                <div className="overline text-primary mb-3">Business Details</div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <dt className="overline text-muted-foreground">Trading Name</dt>
                    <dd className="font-semibold mt-0.5">
                      {profileView.profile.company_name || <span className="text-muted-foreground font-normal">—</span>}
                    </dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">Contact Email</dt>
                    <dd className="font-mono text-xs mt-0.5">
                      {profileView.profile.contact_email || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">Phone</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.phone || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">Website</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.website || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">VAT Number</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.vat_number || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">Company Reg #</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.company_registration || "—"}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="overline text-muted-foreground">Address</dt>
                    <dd className="whitespace-pre-line mt-0.5">
                      {profileView.profile.address || <span className="text-muted-foreground">—</span>}
                    </dd>
                  </div>
                </dl>
              </div>

              <div>
                <div className="overline text-primary mb-3">Bank Details</div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <dt className="overline text-muted-foreground">Bank Name</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.bank_name || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">Account Name</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.account_name || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">Account #</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.account_number || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">Sort Code</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.sort_code || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">IBAN</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.iban || "—"}</dd>
                  </div>
                  <div>
                    <dt className="overline text-muted-foreground">SWIFT / BIC</dt>
                    <dd className="font-mono text-xs mt-0.5">{profileView.profile.swift_bic || "—"}</dd>
                  </div>
                </dl>
              </div>

              {!profileView.profile.company_name && !profileView.profile.bank_name && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
                  This subcontractor hasn't filled in their profile yet. Invoices they issue will show their login name only until they complete it under Settings.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setProfileView(null)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset result dialog */}
      <Dialog open={!!resetInfo} onOpenChange={(o) => !o && setResetInfo(null)}>
        <DialogContent data-testid="reset-result-dialog">
          <DialogHeader>
            <DialogTitle>New Password Generated</DialogTitle>
            <DialogDescription>
              Share this with the subcontractor. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          {resetInfo && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="overline text-muted-foreground">Email</div>
                <div className="font-mono mt-1">{resetInfo.email}</div>
              </div>
              <div className="text-sm">
                <div className="overline text-muted-foreground">New Password</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 font-mono bg-muted px-3 py-2 text-sm border border-border">
                    {resetInfo.newPassword}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyText(resetInfo.newPassword)}
                    className="rounded-sm"
                    data-testid="copy-password-btn"
                  >
                    <Copy size={16} weight="bold" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResetInfo(null)} className="rounded-sm">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this subcontractor?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the account. Their invoices will remain but be labelled by their id.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-sub-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
